import React from "react";
import { useParams } from "react-router";
import { Link as ReactRouterLink } from "react-router-dom";
import type { LinkEvent } from "../analytics";
import { nameForLink, useDispatchLinkClicked } from "../analytics";
import { resolvePathWithSpaceId } from "./resolvePathWithSpaceId";
type HTMLAnchorAttributes = React.AnchorHTMLAttributes<HTMLAnchorElement>;
export const Link = React.forwardRef<HTMLAnchorElement, HTMLAnchorAttributes>(({ className, href, tabIndex, onClick, children, ...other }, ref) => {
    const dispatchLinkClicked = useDispatchLinkClicked();
    // The space id is currently fetched from the route params in order to resolve any space id in the href.
    // Ideally this link would not need to know anything about spaces, and the href passed in would already have the space id resolved.
    // However, because of how routeLinks are structured, they require us to resolve the space id on the fly.
    const routeParams = useParams<{
        spaceId: string | undefined;
    }>();
    const spaceId = routeParams.spaceId ?? undefined;
    const path = href ? resolvePathWithSpaceId(href, spaceId) : undefined;
    const handleLinkClicked: React.MouseEventHandler<HTMLAnchorElement> = React.useCallback((ev) => {
        const linkEvent: LinkEvent = {
            linkLabel: ev.currentTarget.innerText ?? "",
            documentUrl: ev.currentTarget.href,
        };
        dispatchLinkClicked(nameForLink(), linkEvent);
        onClick?.(ev);
    }, [dispatchLinkClicked, onClick]);
    if (path === undefined) {
        return null;
    }
    return (<ReactRouterLink className={className} innerRef={ref} to={path} target="_self" tabIndex={tabIndex} onClick={handleLinkClicked} 
    /*
    We need to spread unknown props here as the design link could be used
    in situations where the props aren't explicit i.e. material menu list items,
    the basic props for regular usage should still be constrained.
    */
    {...other}>
            {children}
        </ReactRouterLink>);
});
